import { useRef, useEffect, useCallback } from 'react';

interface LottiePlayerOptions {
  autoplay?: boolean;
  loop?: boolean;
  speed?: number;
  delay?: number;
}

interface LottiePlayerElement extends HTMLElement {
  stop: () => void;
  play: () => void;
  pause: () => void;
  setSpeed: (speed: number) => void;
}

export const useLottiePlayer = (options: LottiePlayerOptions = {}) => {
  const { autoplay = false, loop = false, speed = 1, delay = 500 } = options;
  const playerRef = useRef<LottiePlayerElement>(null);

  const playOnce = useCallback(async () => {
    if (!playerRef.current) return;

    await window.customElements.whenDefined('lottie-player');
    await new Promise((resolve) => setTimeout(resolve, delay));

    const player = playerRef.current;
    if (player) {
      player.stop?.();
      player.setSpeed?.(speed);

      // Set loop attribute
      if (loop) {
        player.setAttribute('loop', 'true');
      } else {
        player.removeAttribute('loop');
      }

      player.play?.();
    }
  }, [speed, delay, loop]);

  const playLoop = useCallback(async () => {
    if (!playerRef.current) return;

    await window.customElements.whenDefined('lottie-player');
    await new Promise((resolve) => setTimeout(resolve, delay));

    const player = playerRef.current;
    if (player) {
      player.stop?.();
      player.setSpeed?.(speed);
      player.setAttribute('loop', 'true');
      player.play?.();
    }
  }, [speed, delay]);

  useEffect(() => {
    if (!autoplay) return;
    playOnce();
  }, [autoplay, playOnce]);

  return {
    playerRef,
    playOnce,
    playLoop,
    stop: () => playerRef.current?.stop?.(),
    play: () => playerRef.current?.play?.(),
    pause: () => playerRef.current?.pause?.(),
    setSpeed: (newSpeed: number) => playerRef.current?.setSpeed?.(newSpeed),
  };
};
